import React, { useEffect, useState } from "react";
import { addMessage } from "../services/messageService"; // API function
import "../styles/form.css";
import { MessageInputForm, MessageRequest } from "../types/MessageTypes";
import { FamilyResponse } from "../types/FamilyTypes";

interface AddMessageFormProps {
  families: FamilyResponse[];
  loadMessages: () => void;
}

const AddMessageForm: React.FC<AddMessageFormProps> = ({ families, loadMessages }) => {
  const [newMessage, setNewMessage] = useState<MessageRequest>({
    sender: "Sage Pharmacy",
    sentBody: "",
    familyId: 0,
    // targetPhoneNumber: "",
    sourceAPI: "https://www.besthomecare.ca/messages",
    isRecurred: true,
    templateId: null,
    scheduleCycleList: [
      {
        scheduledEndTime: "",
        scheduledStartTime: "",
        scheduleCycle: 0,
        frequency: "Custom",
      }
    ],
  });

  const [messageInput, setMessageInput] = useState<MessageInputForm>({
    sentBody: "",
    familyId: 0,
    isRecurred: true,
    schedules: [
      {
        scheduledStartTime: "",
        numDays: 0
      }
    ]
  });

  // Frequency options matching C# enum
  const frequencyOptions = ["Hourly", "Daily", "Weekly", "Monthly", "Yearly", "Custom"];

  const convertFrequencyText = (frequency: string) => {
    if (frequency === "Custom") {
      return "Minutes";
    }

    return frequency;
  }

  // Handle form input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setMessageInput({ ...messageInput, [e.target.name]: e.target.value });
  };

  // Handle schedule input change (for dynamic multiple schedules)
  const handleScheduleChange = (index: number, e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const updatedSchedules = [...(messageInput.schedules ?? [])];
    updatedSchedules[index] = {
      ...updatedSchedules[index],
      [e.target.name]: e.target.value,
    };
    setMessageInput({ ...messageInput, schedules: updatedSchedules });
  };

  // Add a new empty schedule row
  const handleAddScheduleRow = () => {
    setMessageInput((prev) => ({
      ...prev,
      schedules: [
        ...(prev.schedules ?? []),
        {
          scheduledStartTime: "",
          numDays: 0
        }
      ],
    }));
  };

  // Remove a schedule row
  const handleRemoveScheduleRow = (index: number) => {
    const updatedSchedules = (messageInput.schedules ?? []).filter((_, i) => i !== index);
    setMessageInput({ ...messageInput, schedules: updatedSchedules });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!messageInput.sentBody || messageInput.familyId === 0) {
      alert("Please fill in all required fields.");
      return;
    }

    if (messageInput.isRecurred && (messageInput.schedules == null || messageInput.schedules.length === 0)) {
      alert("Please fill in all required fields.");
      return;
    }
    
    // Convert times to UTC before sending
    const updatedMessage: MessageRequest = {
      sender: "Sage Pharmacy",
      sentBody: messageInput.sentBody,
      familyId: Number(messageInput.familyId),
      sourceAPI: "https://www.besthomecare.ca/messages",
      isRecurred: true,
      scheduleCycleList: messageInput.isRecurred ? (messageInput.schedules ?? []).map(schedule => (
      {
        scheduledStartTime: new Date(schedule.scheduledStartTime).toISOString(),
        scheduledEndTime: calculateEndDate(new Date(schedule.scheduledStartTime), schedule.numDays).toISOString(),
        scheduleCycle: 1,
        frequency: "Daily"
      })) : [],
    };

    const response = await addMessage(updatedMessage);

    if (response) {
      alert("Message added successfully!");
      setMessageInput({
        sentBody: "",
        familyId: 0,
        isRecurred: true,
        schedules: [
          {
            scheduledStartTime: "",
            numDays: 0
          }
        ]
      })
      loadMessages(); // Refresh message list
    } else {
      alert("Failed to add message.");
    }
  };

  const calculateEndDate = (date: Date, numDays: number) => {
    const endDate = new Date(date);
    endDate.setDate(endDate.getDate() + Number(numDays));
    return endDate;
  }

  return (
    <div className="form-container">
      <h2>Register New Message</h2>
      <form onSubmit={handleSubmit}>
        <div className="input-group">
          <textarea name="sentBody" rows={5} placeholder="Message Body" value={messageInput.sentBody} onChange={handleChange} required />
          <select name="familyId" value={messageInput.familyId} onChange={handleChange} required>
            <option value={0}>Please select family</option>
            {families && families.map((family) => (
              <option key={family.id} value={family.id}>
                {family.familyName}
              </option>
            ))}
          </select>          
        </div>

        {messageInput.isRecurred && (
          <div>
            <div className="create-schedule-header">
              <h3>Schedule Recurrence</h3>
              <button type="button" onClick={handleAddScheduleRow}>+ Add Another Schedule</button>
            </div>
            <div className="input-group">
            {messageInput.schedules ? messageInput.schedules.map((schedule, index) => (
                <div key={index} className="input-row schedule-input-row create-schedule">
                  <p>Send a message daily at </p>
                  <input
                    type="datetime-local"
                    name="scheduledStartTime"
                    value={schedule.scheduledStartTime}
                    onChange={(e) => handleScheduleChange(index, e)}
                    required
                  />
                  <p> for </p>
                  <input
                    className="schedule-input"
                    type="number"
                    name="numDays"
                    placeholder="Number of days"
                    value={schedule.numDays}
                    onChange={(e) => handleScheduleChange(index, e)}
                    required
                  />
                  <p> days</p>
                  <div className="button-form-group schedule-input-row-button">
                    <button className="delete-button" type="button" onClick={() => handleRemoveScheduleRow(index)}>Remove</button>
                  </div>
                </div>
              )) : []}              
            </div>
          </div>
        )}

        <button className="submit-button" type="submit">Submit</button>
      </form>
    </div>
  );
};

export default AddMessageForm;
